/**
 * @fileoverview Publish Operations React Query Hooks
 * @description Hooks for publish/unpublish operations and coverage stats
 * @author MCHD Room Scheduler Team
 * @created 08-10-2025 10:41:19
 * @complies @react-frontend.mdc, @tech-stack-documentation.mdc
 */

import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import {
  getCoverageStats,
  bulkPublishAssignments,
  bulkUnpublishAssignments,
  type CoverageStatsParams,
  type CoverageStatsResponse,
  type BulkPublishParams,
  type BulkPublishResponse
} from '../../api/endpoints/publish';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const publishQueryKeys = {
  all: ['publish'] as const,
  coverage: (params: CoverageStatsParams) => [...publishQueryKeys.all, 'coverage', params] as const,
};

// =============================================================================
// COVERAGE STATS HOOK
// =============================================================================

/**
 * Hook to fetch coverage statistics for publish modal
 * @param params - Coverage stats parameters (startDate, endDate, buildingId, floorId)
 * @param enabled - Whether to enable the query
 * @returns Coverage stats query result
 * 
 * UX Spec: Section 3.6 - Display coverage before publish
 * Shows:
 * - Total assignments
 * - Published count
 * - Draft count
 * - Conflicts count
 * - Overall coverage %
 * - Room-by-room breakdown
 * 
 * @example
 * ```tsx
 * const { data, isLoading } = useCoverageStats({
 *   startDate: '2025-10-08',
 *   buildingId: 'building-123'
 * });
 * ```
 * 
 * @created 08-10-2025 10:41:19
 */
export function useCoverageStats(
  params: CoverageStatsParams,
  enabled: boolean = true
): UseQueryResult<CoverageStatsResponse['data'], Error> {
  return useQuery({
    queryKey: publishQueryKeys.coverage(params),
    queryFn: async () => {
      const response = await getCoverageStats(params);
      
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to fetch coverage statistics');
      }
      
      return response.data;
    },
    enabled: enabled && !!params.startDate,
    staleTime: 1 * 60 * 1000, // 1 minute (coverage changes frequently)
    gcTime: 3 * 60 * 1000, // 3 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on validation errors
      if (error?.message?.includes('required') || error?.message?.includes('invalid')) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

// =============================================================================
// PUBLISH MUTATION HOOKS
// =============================================================================

/**
 * Hook to bulk publish assignments
 * @returns Bulk publish mutation
 * 
 * UX Spec: Section 3.6 - Publish schedule for date range
 * - Publishes assignments matching criteria
 * - Handles conflicts based on options
 * - Creates audit log entry
 * - Invalidates assignment queries on success
 * 
 * @example
 * ```tsx
 * const publishMutation = useBulkPublish();
 * 
 * publishMutation.mutate({
 *   date: '2025-10-08',
 *   scope: 'day',
 *   buildingId: 'building-123',
 *   options: { skipConflicts: true }
 * });
 * ```
 * 
 * @created 08-10-2025 10:41:19
 */
export function useBulkPublish() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: BulkPublishParams) => bulkPublishAssignments(params),
    onSuccess: (_data) => {
      // Invalidate all assignment queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ['assignments']
      });
      
      // Invalidate planner queries
      queryClient.invalidateQueries({
        queryKey: ['planner']
      });
      
      // Invalidate dashboard queries (published coverage affected)
      queryClient.invalidateQueries({
        queryKey: ['dashboard']
      });
      
      // Invalidate coverage queries
      queryClient.invalidateQueries({
        queryKey: publishQueryKeys.all
      });
    },
    onError: (error: any) => {
      console.error('Error in bulk publish:', error);
    },
  });
}

/**
 * Hook to bulk unpublish assignments
 * @returns Bulk unpublish mutation
 * 
 * @created 08-10-2025 10:41:19
 */
export function useBulkUnpublish() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: BulkPublishParams) => bulkUnpublishAssignments(params),
    onSuccess: (_data) => {
      // Invalidate all assignment queries
      queryClient.invalidateQueries({
        queryKey: ['assignments']
      });
      
      queryClient.invalidateQueries({
        queryKey: ['planner']
      });
      
      queryClient.invalidateQueries({
        queryKey: ['dashboard']
      });
      
      queryClient.invalidateQueries({
        queryKey: publishQueryKeys.all
      });
    },
    onError: (error: any) => {
      console.error('Error in bulk unpublish:', error);
    },
  });
}

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type {
  CoverageStatsParams,
  CoverageStatsResponse,
  BulkPublishParams,
  BulkPublishResponse
};

